import { hasSessionEvents, legacyEventsWritable } from "../orgs/eventModel";
import type { EventModel } from "../orgs/OrgRecord";

/**
 * The goal types that create events: `create_events` creates dataset events
 * (`ev_`) on the dataset chosen at launch, and `session_create_events` creates
 * session events (`evt_`) on the session chosen at launch.
 */
export type EventsGoalType = "create_events" | "session_create_events";

/**
 * Whether an org on `eventModel` may declare a goal of `goalType`; the server
 * refuses the goal when this is false. Dataset events can be created only
 * while the org's dataset events are writable (`legacy`, `dual`), and session
 * events only once the org has them (`dual`, `migrating`, `session`).
 */
export function eventsGoalAllowed(
  goalType: EventsGoalType,
  eventModel: EventModel,
): boolean {
  return goalType === "create_events"
    ? legacyEventsWritable(eventModel)
    : hasSessionEvents(eventModel);
}

/** Whether `goalType` is one of the goal types that create events. */
export function isEventsGoalType(
  goalType: unknown,
): goalType is EventsGoalType {
  return goalType === "create_events" || goalType === "session_create_events";
}

/**
 * Whether `goalType` is an events goal that an org on `eventModel` may not
 * declare. An unknown event model (the org record has not loaded) refuses
 * nothing, which leaves the decision to the server.
 */
export function eventsGoalRefused(
  goalType: string,
  eventModel: EventModel | undefined,
): boolean {
  return (
    eventModel !== undefined &&
    isEventsGoalType(goalType) &&
    !eventsGoalAllowed(goalType, eventModel)
  );
}
